import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { useAbility } from '../../context/AbilityContext.js';
import { refreshRoleChoices } from '../../hooks/useRoleOptions.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';
import { permissionHint } from './permissionHint.js';
import { scopeLabel } from './scopeLabel.js';

/**
 * 207 (#313): two entirely different renderings depending on the SAME `editable` flag the backend
 * already computed (`GET /roles/:id`) — never re-derived client-side.
 *
 * Editable (Admin/Supplier/Host): the full catalogue as checkboxes grouped by subject, checked
 * against `assigned_permission_ids`. Save is a full-replacement PATCH (research.md R4) — the whole
 * checked set, not a delta — and is visible only when `useAbility().can('manage','Role')` is true,
 * the SAME check the backend route itself makes (`shared/ability.ts`), so frontend and backend
 * agree by construction, not by convention.
 *
 * Protected (SuperAdmin/Customer): `introspected_rules`, read-only, no save control regardless of
 * the caller's own ability — those two roles are never editable via this feature at all.
 */

interface CataloguePermission {
  id: string;
  subject: string;
  action: string;
  scope: 'any' | 'own_supplier';
  description: string | null;
}

interface IntrospectedRule {
  action: string;
  subject: string;
  conditions: Record<string, unknown> | null;
  inverted: boolean;
}

type RoleDetail =
  | {
      id: string;
      name: string;
      editable: true;
      assigned_permission_ids: string[];
      catalogue: CataloguePermission[];
    }
  | {
      id: string;
      name: string;
      editable: false;
      introspected_rules: IntrospectedRule[];
    };

function groupBySubject(catalogue: CataloguePermission[]): Map<string, CataloguePermission[]> {
  const groups = new Map<string, CataloguePermission[]>();
  for (const perm of catalogue) {
    const list = groups.get(perm.subject) ?? [];
    list.push(perm);
    groups.set(perm.subject, list);
  }
  return groups;
}

export function RoleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const ability = useAbility();
  const canEdit = ability.can('manage', 'Role');
  const queryClient = useQueryClient();

  const { data: role, isLoading } = useQuery({
    queryKey: RESOURCE_KEYS.role.one(id ?? ''),
    queryFn: () => api.get<RoleDetail>(`/roles/${id}`),
    enabled: Boolean(id),
    retry: false,
  });

  const [checked, setChecked] = useState<Set<string> | null>(null);
  const assignedSet = checked ?? new Set(role?.editable ? role.assigned_permission_ids : []);

  const save = useMutation({
    mutationFn: (permissionIds: string[]) =>
      api.patch<{ assigned_permission_ids: string[] }>(`/roles/${id}/permissions`, {
        permission_ids: permissionIds,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.role.one(id ?? '') });
      queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.role.list });
      // 367 (FR-003): the user forms' role choices follow at once.
      refreshRoleChoices(queryClient);
      setChecked(null);
    },
  });

  if (isLoading) return <div className="p-4 text-steel">Loading…</div>;
  if (!role) return <div className="p-4 text-steel">Role not found.</div>;

  return (
    <div>
      <PageHeader
        title={role.name}
        description={
          role.editable
            ? 'Assign which permissions this role holds.'
            : 'Protected — this role is not editable.'
        }
        fallback="/roles"
      />

      {role.editable ? (
        <>
          {[...groupBySubject(role.catalogue).entries()].map(([subject, perms]) => (
            <div key={subject} className="mb-6">
              <h2 className="text-sm font-semibold text-ink mb-2">{subject}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {perms.map((perm) => (
                  <label key={perm.id} className="flex items-center gap-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      checked={assignedSet.has(perm.id)}
                      disabled={!canEdit}
                      onChange={(e) => {
                        const next = new Set(assignedSet);
                        if (e.currentTarget.checked) next.add(perm.id);
                        else next.delete(perm.id);
                        setChecked(next);
                      }}
                    />
                    <span>
                      {perm.action}
                      <span className="text-steel"> ({scopeLabel(perm.scope)})</span>
                      {permissionHint(perm.subject, perm.action) && (
                        <>
                          <span className="text-steel"> · </span>
                          <span
                            data-testid={`permission-hint-${perm.subject}-${perm.action}`}
                            className="text-steel"
                          >
                            {permissionHint(perm.subject, perm.action)}
                          </span>
                        </>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          {canEdit && (
            <Button
              onClick={() => save.mutate([...assignedSet])}
              loading={save.isPending}
              disabled={checked === null}
            >
              Save
            </Button>
          )}
        </>
      ) : (
        <div className="space-y-2">
          {role.introspected_rules.map((rule, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: a read-only, server-computed list with no stable id of its own
            <div key={i} className="flex items-center gap-2 text-sm text-ink">
              <Badge variant="success">{rule.action}</Badge>
              <span>{rule.subject}</span>
              {rule.conditions && (
                <span className="text-steel text-xs">{JSON.stringify(rule.conditions)}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
